import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Copy, Mail, ExternalLink, RefreshCw, Share2, KeyRound } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const extractErr = (e, fb = 'Échec') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => (typeof x === 'string' ? x : (x?.msg || JSON.stringify(x)))).join(' · ');
  return JSON.stringify(d);
};

/**
 * Bloc admin affiché dans l'onglet Documents d'une opération.
 * Permet de :
 *   - Générer un code 4 chiffres (envoyé par email à l'admin)
 *   - Afficher le code + l'URL /sign/:opId
 *   - Copier le lien / le code
 *   - Régénérer un nouveau code si besoin
 *
 * PM Spec 30/04/2026 — Q1=b (code envoyé par email)
 */
const ExternalSignCodeBlock = ({ operationId, entityKind = 'operation' }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const token = () => 'cookie-auth';

  const endpointPrefix = entityKind === 'transfer' ? 'transfers' : 'operations';

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/${endpointPrefix}/${operationId}/loading-notes/_generate-sign-code`,
        {},
        {}
      );
      setResult(r.data);
      if (r.data?.email?.success) {
        const recipients = (r.data.email.to || []).join(', ');
        toast({
          title: 'Code généré',
          description: `Email envoyé à ${recipients}${r.data.email.fallback_used ? ' (fallback admin)' : ''}`,
        });
      } else {
        toast({
          title: 'Code généré',
          description: 'Email non envoyé — utilisez le lien/code ci-dessous pour le transmettre.',
        });
      }
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copié', description: label });
    } catch {
      toast({ title: 'Erreur', description: 'Clipboard indisponible', variant: 'destructive' });
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-dashed border-gray-200" data-testid="external-sign-code-block">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Share2 className="w-4 h-4 text-emerald-600" />
          <h4 className="text-sm font-semibold text-gray-800">Signature externe (tablette terrain)</h4>
        </div>
        <Button size="sm" variant={result ? 'outline' : 'default'}
                onClick={handleGenerate} disabled={loading}
                className={result ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                data-testid="generate-sign-code-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : (result ? <RefreshCw className="w-4 h-4 mr-1" /> : <KeyRound className="w-4 h-4 mr-1" />)}
          {result ? 'Régénérer' : 'Générer un code'}
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Générez un code à 4 chiffres pour permettre à l'opérateur terrain de signer les bons
        depuis sa tablette via une page externe (sans se connecter à l'app).
      </p>
      {result ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-3" data-testid="sign-code-result">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-700">Code de signature</div>
              <div className="font-mono text-3xl font-bold text-emerald-800 tracking-[0.25em]"
                   data-testid="sign-code-value">
                {result.code}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => copyText(result.code, 'Code copié')}
                    data-testid="copy-code-btn">
              <Copy className="w-4 h-4 mr-1" /> Copier
            </Button>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 mb-1">Lien tablette</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-emerald-100 rounded px-2 py-1 text-xs text-gray-700 break-all"
                    data-testid="sign-url-value">
                {result.signUrl}
              </code>
              <Button size="sm" variant="ghost" onClick={() => copyText(result.signUrl, 'Lien copié')}
                      data-testid="copy-url-btn">
                <Copy className="w-4 h-4" />
              </Button>
              <a href={result.signUrl} target="_blank" rel="noreferrer"
                 className="inline-flex items-center text-xs text-emerald-700 hover:underline"
                 data-testid="open-sign-url">
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className="flex items-center justify-between text-[11px] text-emerald-700 pt-2 border-t border-emerald-100">
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {result.email?.success ? (
                <span>
                  Email envoyé à {(result.email.to || []).length} destinataire(s) :{' '}
                  <strong>{(result.email.to || []).join(', ')}</strong>
                  {result.email.fallback_used && <span className="text-amber-700 ml-1">(fallback admin)</span>}
                </span>
              ) : (
                <span className="text-amber-700">
                  Email non délivré — transmettez le code manuellement (SMS/WhatsApp)
                  {result.email?.failed?.length > 0 && (
                    <span className="block text-[10px]">Échecs: {result.email.failed.join(', ')}</span>
                  )}
                </span>
              )}
            </div>
            <Badge variant="outline" className="text-[10px]">
              Valide tant que l'op. n'est pas clôturée
            </Badge>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-400 italic">Aucun code actif pour le moment.</div>
      )}
    </div>
  );
};

export default ExternalSignCodeBlock;
